import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface User {
  id: number;
  name: string;
  email: string;
  role?: {
    id: number;
    name: string;
    slug: string;
  } | null;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  permissions: string[];
  hasPermission: (permissionSlug: string) => boolean;
  hasModulePermission: (modulePrefix: string) => boolean;
  logout: () => void;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem("user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [permissions, setPermissions] = useState<string[]>(() => {
    try {
      const savedPerms = localStorage.getItem("permissions");
      return savedPerms ? JSON.parse(savedPerms) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (user?.permissions) {
      setPermissions(user.permissions);
    }
  }, [user]);

  const hasPermission = (permissionSlug: string): boolean => {
    if (!user) {
      return false;
    }

    // If super admin (email admin@gmail.com, or wildcard '*' permission, or role slug 'admin'), allow everything
    if (
      user.email === "admin@gmail.com" ||
      user.role?.slug === "admin" ||
      permissions.includes("*")
    ) {
      return true;
    }

    return permissions.includes(permissionSlug);
  };

  const hasModulePermission = (modulePrefix: string): boolean => {
    if (!user) {
      return false;
    }

    if (
      user.email === "admin@gmail.com" ||
      user.role?.slug === "admin" ||
      permissions.includes("*")
    ) {
      return true;
    }

    return permissions.some((p) => p.startsWith(modulePrefix));
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    localStorage.removeItem("login");
    setUser(null);
    setPermissions([]);
  };

  return (
    <AuthContext.Provider value={{ user, permissions, hasPermission, hasModulePermission, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
